/**
 * SlotDetail — полноценная страница деталей слота.
 *
 * URL: /slots/:slotId (photon, comet, asteroid...)
 * Показывает ВСЕ контракты этого типа, каждый с:
 *   - Прогресс 250%, заработано, дней активен
 *   - PC статус (on/off/expiring)
 *   - Кнопка "Установить PC" для каждого контракта БЕЗ PC
 *   - Кнопка перезарядки для контрактов с истекающим PC
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, type Variants } from 'motion/react';
import { ArrowLeft, Zap, Plus, Shield, Clock, Gift } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlassSurface, GlassPill } from '@/components/glass';
import { PlasmaCorePurchaseModal } from '@/components/slots/PlasmaCorePurchaseModal';
import { SlotDepositModal } from '@/components/slots/SlotDepositModal';
import { VENTURE_SLOTS } from '@/data/ventureSlots';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useAuth } from '@/contexts/AuthContext';
import { lunaApi } from '@/services/api';

interface ContractData {
  id: string;
  slotName: string;
  totalPaidOut: string;
  createdAt: string;
  hasPlasmaCore: boolean;
  status: string;
  source?: 'purchase' | 'reinvest' | 'quick_start_bonus';
}

const containerV: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { delayChildren: 0.05, staggerChildren: 0.08 } },
};
const itemV: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.32, 0.72, 0, 1] } },
};

export function SlotDetail() {
  const { t } = useTranslation();
  const { slotId } = useParams<{ slotId: string }>();
  const navigate = useNavigate();
  const { haptic } = useTelegramWebApp();
  const { user: authUser, refresh: refreshAuth } = useAuth();

  const slot = VENTURE_SLOTS.find(s => s.id === slotId);
  const [contracts, setContracts] = useState<ContractData[]>([]);
  const [pcTargetIds, setPcTargetIds] = useState<string[]>([]);
  const [depositOpen, setDepositOpen] = useState(false);

  const fetchContracts = useCallback(() => {
    lunaApi.slots.my().then((res) => {
      const d = res.data as { success: boolean; contracts?: ContractData[] };
      if (d.success && d.contracts) {
        setContracts(
          d.contracts.filter(c =>
            c.slotName.toLowerCase() === slotId && c.status === 'active'
          ),
        );
      }
    }).catch(() => {});
  }, [slotId]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  if (!slot) {
    return (
      <div className="flex items-center justify-center h-64 text-[var(--text-muted)]">
        {t('slotDetail.notFound')}
      </div>
    );
  }

  const hue = slot.hue;
  const accent = `hsl(${hue} 80% 65%)`;
  const accentGlow = `hsl(${hue} 80% 60% / 0.4)`;
  const perSlotCap = slot.amount * 2.5;
  const totalEarned = contracts.reduce((s, c) => s + parseFloat(c.totalPaidOut), 0);
  const totalCap = perSlotCap * Math.max(1, contracts.length);
  const userBalance = parseFloat(authUser?.usdtBalance ?? '0');

  return (
    <>
      <motion.div
        className="space-y-4 max-w-md mx-auto pt-2 pb-6"
        variants={containerV}
        initial="hidden"
        animate="show"
      >
        {/* Header with back button */}
        <motion.div variants={itemV} className="flex items-center gap-3">
          <button
            onClick={() => { haptic.light(); navigate('/slots'); }}
            className="h-10 w-10 rounded-2xl flex items-center justify-center transition-all active:scale-[0.92]"
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
            }}
          >
            <ArrowLeft size={18} className="text-[var(--text-secondary)]" />
          </button>
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-mono">
              {t('slotDetail.contractLine', { month: slot.unlockMonth })}
            </p>
            <h1 className="font-display text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight">
              {slot.name}
            </h1>
          </div>
        </motion.div>

        {/* Hero card */}
        <motion.div variants={itemV}>
          <GlassSurface variant="strong" className="p-5 relative overflow-hidden">
            <div
              aria-hidden
              className="absolute pointer-events-none"
              style={{
                top: -50, right: -50,
                width: 200, height: 200,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${accentGlow} 0%, transparent 60%)`,
              }}
            />
            <div className="relative flex items-center gap-4 mb-4">
              <div
                className="flex-shrink-0 flex items-center justify-center rounded-[24px]"
                style={{
                  width: 80, height: 80,
                  background: `hsl(${hue} 70% 55% / 0.18)`,
                  border: `1.5px solid ${accent}`,
                  boxShadow: `0 0 40px -8px ${accentGlow}`,
                }}
              >
                <img src={slot.icon} alt={slot.name} width={56} height={56} />
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-[var(--text-secondary)]">{slot.tagline}</p>
                <div className="flex items-baseline gap-1.5 mt-1">
                  <span className="font-display text-[28px] font-extrabold tabular-nums" style={{ color: accent }}>
                    {slot.amount}
                  </span>
                  <span className="text-[12px] text-[var(--text-muted)] font-bold">USD₮</span>
                </div>
              </div>
              <GlassPill tone="positive" dot compact>
                ×{contracts.length}
              </GlassPill>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              <MiniStat label={t('slotDetail.yield')} value={t('slotDetail.yieldValue', { rate: slot.monthlyRate })} accent={accent} />
              <MiniStat label={t('slotDetail.perDay')} value={t('slotDetail.perDayValue', { rate: slot.dailyRate })} />
              <MiniStat label={t('slotDetail.cap')} value={t('slotDetail.capValue')} />
            </div>

            {/* Progress */}
            {contracts.length > 0 && (
              <div className="mt-4">
                <div className="flex justify-between mb-1">
                  <span className="text-[9px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono">
                    {t('slotDetail.progressSum')}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-[var(--text-secondary)] tabular-nums">
                    {totalEarned > 0 ? totalEarned > 0 ? parseFloat(totalEarned.toFixed(3)) : 0 : '0'} / {totalCap}
                  </span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--glass-stroke)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min(100, (totalEarned / totalCap) * 100)}%`,
                      background: `linear-gradient(90deg, ${accent}, var(--accent-secondary))`,
                      boxShadow: `0 0 8px ${accentGlow}`,
                    }}
                  />
                </div>
              </div>
            )}
          </GlassSurface>
        </motion.div>

        {/* Contracts list */}
        <motion.div variants={itemV}>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-mono mb-2 px-1">
            {t('slotDetail.yourSlots', { count: contracts.length })}
          </p>
        </motion.div>

        {contracts.map((contract, idx) => {
          const earned = parseFloat(contract.totalPaidOut);
          const pct = Math.min(100, (earned / perSlotCap) * 100);
          const daysActive = Math.max(1, Math.round(
            (Date.now() - new Date(contract.createdAt).getTime()) / 86_400_000,
          ));

          return (
            <motion.div key={contract.id} variants={itemV}>
              <GlassSurface
                className="p-4 relative"
                style={contract.hasPlasmaCore ? {
                  borderColor: 'color-mix(in oklab, var(--color-plasma-400) 25%, var(--glass-stroke))',
                  boxShadow: '0 0 24px -6px var(--color-plasma-500), inset 0 0 20px -10px color-mix(in oklab, var(--color-plasma-400) 15%, transparent)',
                } : undefined}
              >

                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="h-8 w-8 rounded-xl flex items-center justify-center text-[11px] font-mono font-extrabold"
                      style={{
                        background: contract.hasPlasmaCore
                          ? 'color-mix(in oklab, var(--color-plasma-400) 15%, transparent)'
                          : `hsl(${hue} 70% 55% / 0.15)`,
                        color: contract.hasPlasmaCore ? 'var(--color-plasma-400)' : accent,
                        border: contract.hasPlasmaCore
                          ? '1px solid color-mix(in oklab, var(--color-plasma-400) 30%, transparent)'
                          : `1px solid ${accent}40`,
                      }}
                    >
                      {contract.hasPlasmaCore ? <Zap size={14} strokeWidth={2.2} /> : idx + 1}
                    </span>
                    <div>
                      <p className="text-[13px] font-bold text-[var(--text-primary)] flex items-center gap-1.5">
                        {slot.name}
                        <span className="text-[10px] text-[var(--text-muted)] font-mono">#{idx + 1}</span>
                        {contract.source === 'quick_start_bonus' && (
                          <span
                            className="inline-flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[8.5px] font-mono font-extrabold uppercase tracking-[0.08em]"
                            style={{
                              background: 'color-mix(in oklab, var(--accent-primary) 15%, transparent)',
                              color: 'var(--accent-primary)',
                              border: '1px solid color-mix(in oklab, var(--accent-primary) 35%, transparent)',
                            }}
                          >
                            <Gift size={9} strokeWidth={2.6} />
                            {t('slotDetail.bonus')}
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-[var(--text-muted)]">
                        {t('slotDetail.daysAndAmount', { days: daysActive, amount: slot.amount })}
                      </p>
                    </div>
                  </div>

                  {/* PC status badge */}
                  {contract.hasPlasmaCore ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-mono font-extrabold uppercase tracking-[0.1em]"
                      style={{
                        background: 'color-mix(in oklab, var(--color-plasma-400) 15%, transparent)',
                        color: 'var(--color-plasma-400)',
                        border: '1px solid color-mix(in oklab, var(--color-plasma-400) 35%, transparent)',
                      }}
                    >
                      <Zap size={10} strokeWidth={2.5} />
                      {t('slotDetail.hasPC')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-mono font-bold uppercase tracking-[0.1em] text-[var(--text-disabled)] border border-[var(--glass-stroke)]">
                      {t('slotDetail.noPC')}
                    </span>
                  )}
                </div>

                {/* Progress bar */}
                <div className="mb-3">
                  <div className="flex justify-between mb-1">
                    <span className="text-[9px] text-[var(--text-muted)] font-mono uppercase tracking-[0.12em]">
                      {t('slotDetail.progress25x')}
                    </span>
                    <span className="text-[10px] font-mono font-bold text-[var(--text-secondary)] tabular-nums">
                      {earned > 0 ? parseFloat(earned.toFixed(3)) : '0'} / {perSlotCap}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--glass-stroke)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${pct}%`,
                        background: contract.hasPlasmaCore
                          ? 'linear-gradient(90deg, var(--color-plasma-400), var(--color-plasma-600))'
                          : `linear-gradient(90deg, ${accent}, var(--accent-secondary))`,
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-[9px] font-mono text-[var(--text-disabled)]">
                      {pct.toFixed(1)}%
                    </span>
                    <span className="text-[9px] font-mono text-[var(--text-disabled)]">
                      {contract.hasPlasmaCore ? t('slotDetail.autoClaim24') : t('slotDetail.manualClaim24')}
                    </span>
                  </div>
                </div>

                {/* PC features for contracts with PC */}
                {contract.hasPlasmaCore && (
                  <div className="flex gap-2 mb-3">
                    <PCFeaturePill icon={<Zap size={10} />} text={t('slotDetail.pcAutoClaim')} />
                    <PCFeaturePill icon={<Shield size={10} />} text={t('slotDetail.pcPlus25')} />
                    <PCFeaturePill icon={<Clock size={10} />} text={t('slotDetail.pcDays7')} />
                  </div>
                )}

                {/* PC action button */}
                {!contract.hasPlasmaCore && (
                  <button
                    type="button"
                    onClick={() => { haptic.medium(); setPcTargetIds([contract.id]); }}
                    className="w-full rounded-2xl py-3 flex items-center justify-center gap-2 font-display text-[12px] font-extrabold uppercase tracking-[0.06em] transition-all active:scale-[0.97]"
                    style={{
                      background: 'linear-gradient(180deg, color-mix(in oklab, var(--color-plasma-400) 20%, transparent), color-mix(in oklab, var(--color-plasma-400) 6%, transparent))',
                      border: '1px solid color-mix(in oklab, var(--color-plasma-400) 40%, transparent)',
                      color: 'var(--color-plasma-400)',
                    }}
                  >
                    <Zap size={14} strokeWidth={2.2} />
                    {t('slotDetail.installPC', { cost: Math.round(slot.amount * 0.2) })}
                  </button>
                )}
              </GlassSurface>
            </motion.div>
          );
        })}

        {contracts.length === 0 && (
          <motion.div variants={itemV}>
            <GlassSurface className="p-6 text-center">
              <p className="text-[var(--text-muted)] text-[13px]">
                {t('slotDetail.noContracts')}
              </p>
            </GlassSurface>
          </motion.div>
        )}

        {/* Buy more CTA */}
        <motion.div variants={itemV}>
          <button
            onClick={() => { haptic.medium(); setDepositOpen(true); }}
            className="w-full rounded-2xl py-3.5 flex items-center justify-center gap-2 font-display text-[12px] font-extrabold uppercase tracking-[0.06em] transition-all active:scale-[0.97]"
            style={{
              background: `linear-gradient(180deg, color-mix(in oklab, ${accent} 22%, transparent), color-mix(in oklab, ${accent} 6%, transparent))`,
              border: `1px solid color-mix(in oklab, ${accent} 40%, transparent)`,
              color: accent,
            }}
          >
            <Plus size={14} strokeWidth={2.2} />
            {t('slotDetail.openMore', { name: slot.name })}
          </button>
        </motion.div>
      </motion.div>

      {/* PC Purchase Modal */}
      <PlasmaCorePurchaseModal
        open={pcTargetIds.length > 0}
        contractIds={pcTargetIds}
        slotName={slot.name}
        slotPrice={slot.amount}
        userBalance={userBalance}
        onClose={() => setPcTargetIds([])}
        onSuccess={() => {
          setPcTargetIds([]);
          fetchContracts();
          void refreshAuth();
        }}
      />

      {/* Deposit Modal — "Открыть ещё {slot.name}" */}
      <SlotDepositModal
        slot={depositOpen ? slot : null}
        onClose={() => setDepositOpen(false)}
        onConfirm={() => {
          setDepositOpen(false);
          fetchContracts();
          void refreshAuth();
        }}
      />
    </>
  );
}

function MiniStat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="lg-pill !rounded-2xl px-3 py-2 flex flex-col gap-0.5">
      <span className="text-[8px] uppercase tracking-[0.14em] text-[var(--text-muted)] font-mono font-bold">
        {label}
      </span>
      <span
        className="font-mono text-[12px] font-extrabold tabular-nums"
        style={{ color: accent ?? 'var(--text-primary)' }}
      >
        {value}
      </span>
    </div>
  );
}

function PCFeaturePill({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[8px] font-mono font-bold uppercase tracking-[0.1em]"
      style={{
        background: 'color-mix(in oklab, var(--color-plasma-400) 8%, transparent)',
        color: 'var(--color-plasma-400)',
        border: '1px solid color-mix(in oklab, var(--color-plasma-400) 18%, transparent)',
      }}
    >
      {icon}
      {text}
    </span>
  );
}
